import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const UPDATED_AT = '5 August 2026';
const CONTACT_EMAIL = 'siyamcela@shiftwagon.co.za';

type PolicySection = {
  title: string;
  body?: string;
  items?: string[];
};

const sections: PolicySection[] = [
  {
    title: 'Who this policy covers',
    body:
      'This policy covers ShiftWagon customer, driver, company owner, and admin web and mobile experiences operated by Mike Innovations (Pty) Ltd in South Africa. It applies to the ShiftWagon customer app, ShiftWagon Driver app, ShiftWagon website, support channels, and related backend services.',
  },
  {
    title: 'Information customers may provide or generate',
    items: [
      'Account details such as name, email address, phone number, language preference, login credentials, two-factor settings, verification status, and internal user identifiers.',
      'Booking details such as pickup and dropoff addresses, precise coordinates, saved places, route information, vehicle type, scheduled time, trip status, load notes, contact names and phone numbers for pickup or dropoff, cancellation details, and proof or support attachments.',
      'Payment method metadata such as payment provider, tokenized card reference, card brand, last four digits, default-card status, payment authorization status, refunds, and transaction references. ShiftWagon does not store full card numbers, CVV, card PINs, or raw payment credentials in its app services.',
      'Profile and verification material such as profile photos, face verification images, uploaded delivery photos, message attachments, and support request content.',
      'Communication data such as in-app messages, support inquiries, notification preferences, push notification tokens, and device metadata needed to deliver notifications.',
    ],
  },
  {
    title: 'Information drivers and transport partners may provide or generate',
    items: [
      'Driver account details such as name, email address, phone number, account type, login credentials, verification status, consent records, two-factor settings, communication preferences, and internal driver identifiers.',
      'Driver compliance information such as face verification images, profile photo, ID or personal documents, criminal record checks, PDP or professional driving permit documents, vehicle photos, licence disk, roadworthy documents, insurance details, and related validity dates.',
      'Vehicle and operating details such as vehicle type, registration number, make, model, year, colour, business-use insurance confirmation, operating town, company relationship, ratings, acceptance metrics, completion metrics, and availability state.',
      'Location data such as home base, last known availability location, online/offline status, live trip location, background trip location while an accepted trip is active, route phase, ETA signals, and location quality or trust indicators.',
      'Banking and payout information such as bank name, account holder, encrypted account number, account hash, branch code, account type, verification status, Paystack subaccount references, split percentage, payout readiness, earnings, and payout records.',
    ],
  },
  {
    title: 'How we use information',
    items: [
      'Create and secure user, driver, company owner, and admin accounts.',
      'Verify identity, driver eligibility, vehicle suitability, banking details, and platform safety requirements.',
      'Create, price, dispatch, track, complete, cancel, refund, and support bookings and deliveries.',
      'Show customers driver and vehicle details needed to choose and complete a trip, and show drivers customer and delivery details needed to perform the service.',
      'Process tokenized card payments, preauthorizations, captures, reversals, refunds, split settlement, platform fees, payout readiness, and payment reconciliation.',
      'Send transactional emails, SMS or OTP messages, app notifications, payment notices, trip updates, support messages, and operational alerts.',
      'Maintain audit logs, consent records, fraud prevention signals, diagnostic logs, service reliability, legal records, tax records, and dispute handling records.',
    ],
  },
  {
    title: 'Location sharing',
    body:
      'Customers use location mainly for pickup, dropoff, saved places, route display, and live driver tracking during trips. The customer app may ask for foreground location when a customer chooses a current-location shortcut; customers can also enter or search addresses manually. Drivers share foreground availability location while online and live trip location while performing an accepted trip. The driver app may use background location during an active trip so customers can track progress if the driver locks the phone or opens navigation. Background trip tracking is intended to stop when the driver is no longer on an operationally active trip.',
  },
  {
    title: 'Contacts and device address books',
    body:
      'ShiftWagon may process contact details that users type into the platform, such as account phone numbers, pickup or dropoff contact names, and operational contact numbers needed for a trip. The customer app does not currently request broad access to the device address book or collect device contacts through Android Contacts permissions. If a future feature adds contact-picker or address-book access, this policy and the app-store disclosures should be updated before release.',
  },
  {
    title: 'Service providers and third parties',
    body:
      'We use service providers to run the platform. These providers may process data for hosting, storage, maps, payments, push notifications, email, SMS, security, diagnostics, analytics, and customer support. Current platform integrations include cloud hosting and storage, Google Maps or Places, TomTom route services where configured, Paystack for payment processing and subaccount settlement, Expo/FCM/APNs-style push notification infrastructure, Sentry diagnostics, Google Analytics for website usage measurement, email/SMS/OTP providers, and internal microservices.',
  },
  {
    title: 'Website analytics',
    body:
      'The ShiftWagon website uses Google Analytics to measure how the public site is used, for example how many visitors request a route estimate, which vehicle classes they look at, and how often the estimate service fails. This applies to the website only; the mobile apps use Sentry diagnostics as described above. Analytics events record the shape of an interaction rather than its content, such as a rounded trip distance, the vehicle class selected, whether a route exceeded the instant-trip limit, and which button was used. Pickup and dropoff addresses, map coordinates, names, and contact details are never sent to analytics. Google Analytics additionally sets cookies and receives standard web request information such as IP address, approximate location derived from it, device and browser type, referring page, and pages viewed. A consent mechanism for non-essential analytics cookies is not yet implemented on the website. That should be resolved, and this section reviewed by legal counsel, before broad public launch.',
  },
  {
    title: 'When information is shared',
    items: [
      'With customers and drivers as needed to arrange, perform, track, support, and complete bookings.',
      'With company owners or fleet administrators where a driver is linked to a company account and the information is needed for operations, compliance, payout setup, or review.',
      'With payment providers and banks to tokenize cards, authorize payments, process refunds, manage subaccounts, settle driver or company shares, and reconcile transactions.',
      'With infrastructure, diagnostics, messaging, notification, map, email, and SMS providers acting as service providers for ShiftWagon.',
      'With regulators, courts, law enforcement, payment networks, insurers, or professional advisers where required or reasonably necessary for legal, safety, tax, fraud, dispute, or enforcement purposes.',
    ],
  },
  {
    title: 'Security and retention',
    body:
      'We use role-based access, authentication, encrypted transport, tokenized payment references, encrypted bank-account storage, signed file access, audit records, and operational access controls. We retain information for as long as needed to provide the platform, maintain safety and audit evidence, meet payment, tax, dispute, legal, and regulatory obligations, and resolve support issues. Some records may be retained after account deletion where required for legal, financial, fraud prevention, safety, or dispute reasons.',
  },
  {
    title: 'Your choices',
    items: [
      'You can update profile, communication, saved place, payment, bank, vehicle, and document information in the relevant app where the feature is available.',
      'You can disable non-essential marketing communications through communication preferences or by contacting us.',
      'You can limit website analytics by blocking or deleting analytics cookies in your browser settings, by using a browser that blocks them by default, or by installing Google’s analytics opt-out browser add-on.',
      'You can request account deletion in the mobile apps or through the public data deletion page.',
      'You can contact us to ask for access, correction, objection, restriction, or deletion of personal information, subject to identity verification and legal retention requirements.',
    ],
  },
  {
    title: 'Contact',
    body: `For privacy questions, data requests, or safety concerns, contact ${CONTACT_EMAIL}. This policy is intended to support POPIA-aligned transparency and app-store disclosure, but it should be reviewed by legal counsel before final production launch.`,
  },
];

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="landing-hero landing-hero-simple policy-hero">
          <div className="container policy-hero-content">
            <span className="landing-badge">Privacy Policy</span>
            <h1 className="landing-heading">
              How ShiftWagon handles platform, trip, payment, driver, and support data.
            </h1>
            <p className="landing-subheading">
              Last updated: {UPDATED_AT}. This page is the public privacy policy for ShiftWagon
              customer, driver, company owner, and web experiences.
            </p>
          </div>
        </section>

        <section className="landing-section policy-section">
          <div className="container policy-layout">
            <aside className="policy-summary">
              <h2>Quick Summary</h2>
              <p>
                ShiftWagon collects data needed to verify accounts, coordinate transport,
                process tokenized payments, support drivers and customers, send notifications,
                protect the platform, and maintain legal and financial records.
              </p>
              <a className="landing-primary-action" href="/data-deletion">
                Request Deletion
              </a>
            </aside>

            <div className="policy-content">
              {sections.map((section) => (
                <article className="policy-card" key={section.title}>
                  <h2>{section.title}</h2>
                  {section.body ? <p>{section.body}</p> : null}
                  {section.items ? (
                    <ul>
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
